/**
 * ContractStep - Step 3 of the Add Plane wizard
 *
 * Allows users to:
 * - Choose between Transient or Long-term contract types
 * - Enter minimal required contract details
 */

import { useEffect } from 'react'
import { Calendar, FileText, Clock, DollarSign, Fuel } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  useAddPlaneWizardStore,
  type TransientContractData,
  type LeaseContractData,
} from '@/stores/addPlaneWizardStore'
import { usePricingResolution } from '@/hooks/usePricing'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { PRICING_SOURCE_LABELS } from '@/types/pricing'

// Contract type options
const CONTRACT_TYPES = [
  {
    type: 'transient' as const,
    title: 'Transient',
    description: 'Short-term stay, daily rates',
    icon: Clock,
  },
  {
    type: 'long_term' as const,
    title: 'Long-term Lease',
    description: 'Monthly contract, hangar rental',
    icon: FileText,
  },
]

// Lease type options
const LEASE_TYPES = [
  { value: 'month_to_month', label: 'Month-to-Month' },
  { value: 'long_term', label: 'Fixed Term' },
  { value: 'seasonal', label: 'Seasonal' },
]

// Booking type options
const BOOKING_TYPES = [
  { value: 'flex', label: 'Flexible', description: 'May be relocated if needed' },
  { value: 'guaranteed', label: 'Guaranteed', description: 'Reserved spot guaranteed' },
]

// Rate type options for transient stays
const RATE_TYPES = [
  { value: 'per_night', label: 'Per Night' },
  { value: 'flat', label: 'Flat Rate' },
  { value: 'per_sqft', label: 'Per Sq Ft' },
]

// Rate basis options for leases
const RATE_BASIS_OPTIONS = [
  { value: 'flat', label: 'Flat Rate' },
  { value: 'per_sqft', label: 'Per Sq Ft' },
]

// Fuel type options
const FUEL_TYPES = [
  { value: 'jet_a', label: 'Jet-A' },
  { value: 'avgas', label: 'AvGas' },
]

// Fuel price type options
const FUEL_PRICE_TYPES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'opis_plus', label: 'OPIS Plus' },
  { value: 'retail_discount', label: 'Retail Discount' },
]

export function ContractStep() {
  const { contractData, setContractData, wizardContext, suggestedFuelType, linkedLeaseContractId } = useAddPlaneWizardStore()
  const { locationId } = useAppContext()

  // Resolve suggested pricing for transient parking
  const { data: pricingSuggestion } = usePricingResolution(
    contractData.type === 'transient' && locationId
      ? {
          locationId,
          catalogItemCode: 'parking_overnight',
        }
      : null
  )

  // Auto-fill fuel type from aircraft selection when switching to lease
  useEffect(() => {
    if (
      contractData.type === 'long_term' &&
      contractData.fuel_type === undefined &&
      suggestedFuelType
    ) {
      setContractData({ ...contractData, fuel_type: suggestedFuelType })
    }
  }, [contractData.type, suggestedFuelType]) // eslint-disable-line react-hooks/exhaustive-deps

  // When source is 'transient' or 'lease', the contract type is locked
  const isTypeLocked = wizardContext.source === 'transient' || wizardContext.source === 'lease'

  const isTransient = contractData.type === 'transient'

  // Handle contract type toggle
  const handleTypeChange = (type: 'transient' | 'long_term') => {
    if (type === 'transient') {
      setContractData({
        type: 'transient',
        check_in: new Date().toISOString().split('T')[0],
        expected_check_out: new Date(Date.now() + 86400000)
          .toISOString()
          .split('T')[0],
        booking_type: 'flex',
        rate_type: 'per_night',
        rate_amount: null,
      } as TransientContractData)
    } else {
      const today = new Date()
      const oneYearLater = new Date(today)
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1)
      setContractData({
        type: 'long_term',
        start_date: today.toISOString().split('T')[0],
        end_date: oneYearLater.toISOString().split('T')[0],
        lease_type: 'month_to_month',
        monthly_rate: 0,
        rate_basis: 'flat',
        security_deposit: null,
        fuel_type: suggestedFuelType ?? undefined,
      } as LeaseContractData)
    }
  }

  // Handle transient field changes
  const handleTransientChange = (
    field: keyof TransientContractData,
    value: string | number | null
  ) => {
    if (contractData.type !== 'transient') return
    setContractData({
      ...contractData,
      [field]: value,
    } as TransientContractData)
  }

  // Handle lease field changes
  const handleLeaseChange = (
    field: keyof LeaseContractData,
    value: string | number | null
  ) => {
    if (contractData.type !== 'long_term') return
    setContractData({
      ...contractData,
      [field]: value,
    } as LeaseContractData)
  }

  return (
    <div className="space-y-6">
      {/* Existing-lease notice — this aircraft's owner already leases here; the stay will use it */}
      {linkedLeaseContractId && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
          <FileText className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Existing lease found</div>
            <div className="text-xs">
              This aircraft&apos;s owner holds an active lease at this location. The stay will be
              linked to the existing lease contract — no new transient stay is created.
            </div>
          </div>
        </div>
      )}

      {/* Contract Type Selection — locked when opened from Transient/Lease pages */}
      {isTypeLocked ? (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
          {contractData.type === 'transient' ? (
            <Clock className="w-5 h-5 text-primary" />
          ) : (
            <FileText className="w-5 h-5 text-primary" />
          )}
          <div>
            <div className="font-bold">
              {contractData.type === 'transient' ? 'Transient' : 'Long-term Lease'}
            </div>
            <div className="text-xs text-muted-foreground">
              {contractData.type === 'transient'
                ? 'Short-term stay, daily rates'
                : 'Monthly contract, hangar rental'}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {CONTRACT_TYPES.map(({ type, title, description }) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeChange(type)}
              className={cn(
                'p-2 rounded-xl border-2 flex flex-col items-center gap-4 transition-all text-left',
                contractData.type === type
                  ? 'border-primary bg-primary/5 shadow-lg ring-2 ring-primary/20'
                  : 'border-border bg-card hover:bg-muted/50 hover:border-muted-foreground/30'
              )}
            >
              <div className="text-center">
                <div
                  className={cn(
                    'font-bold transition-colors',
                    contractData.type === type
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                >
                  {title}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {description}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Transient Form */}
      {isTransient && contractData.type === 'transient' && (
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
            <Calendar className="w-4 h-4" />
            Transient Stay Details
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Check-in Date *</Label>
              <Input
                type="date"
                value={contractData.check_in}
                onChange={(e) =>
                  handleTransientChange('check_in', e.target.value)
                }
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Expected Departure *</Label>
              <Input
                type="date"
                value={contractData.expected_check_out}
                onChange={(e) =>
                  handleTransientChange('expected_check_out', e.target.value)
                }
                min={contractData.check_in}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Booking Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {BOOKING_TYPES.map(({ value, label, description }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleTransientChange('booking_type', value)}
                  className={cn(
                    'p-3 rounded-lg border text-left transition-all',
                    contractData.booking_type === value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/30'
                  )}
                >
                  <div className="font-medium text-sm">{label}</div>
                  <div className="text-xs text-muted-foreground">
                    {description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Rate Fields */}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
              <DollarSign className="w-4 h-4" />
              Pricing (Optional)
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate Type</Label>
                <Select
                  value={contractData.rate_type || 'per_night'}
                  onValueChange={(value) =>
                    handleTransientChange('rate_type', value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select rate type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_TYPES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rate Amount ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={contractData.rate_amount ?? ''}
                  onChange={(e) =>
                    handleTransientChange(
                      'rate_amount',
                      e.target.value ? parseFloat(e.target.value) : null
                    )
                  }
                />
              </div>
            </div>
            {pricingSuggestion?.unit_price != null && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Suggested: ${Number(pricingSuggestion.unit_price).toFixed(2)}/night
                {pricingSuggestion.source_detail?.policy_name
                  ? ` (from ${pricingSuggestion.source_detail.policy_name})`
                  : ` (${PRICING_SOURCE_LABELS[pricingSuggestion.source]})`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Long-term Lease Form */}
      {!isTransient && contractData.type === 'long_term' && (
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
            <FileText className="w-4 h-4" />
            Lease Contract Details
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={contractData.start_date}
                onChange={(e) =>
                  handleLeaseChange('start_date', e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label>End Date (Optional)</Label>
              <Input
                type="date"
                value={contractData.end_date || ''}
                onChange={(e) =>
                  handleLeaseChange('end_date', e.target.value || undefined as never)
                }
                min={contractData.start_date}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Lease Type</Label>
            <Select
              value={contractData.lease_type}
              onValueChange={(value) => handleLeaseChange('lease_type', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select lease type..." />
              </SelectTrigger>
              <SelectContent>
                {LEASE_TYPES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Fields */}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
              <DollarSign className="w-4 h-4" />
              Pricing
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monthly Rate ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={contractData.monthly_rate || ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'monthly_rate',
                      e.target.value ? parseFloat(e.target.value) : 0
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Rate Basis</Label>
                <Select
                  value={contractData.rate_basis || 'flat'}
                  onValueChange={(value) =>
                    handleLeaseChange('rate_basis', value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select rate basis..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_BASIS_OPTIONS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Security Deposit ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Optional"
                  value={contractData.security_deposit ?? ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'security_deposit',
                      e.target.value ? parseFloat(e.target.value) : null
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Fuel Allowance (Optional) */}
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
              <Fuel className="w-4 h-4" />
              Fuel Allowance (Optional)
            </div>
            {/* Row 1: Fuel Type */}
            <div className="space-y-2 mb-4">
              <Label>Fuel Type</Label>
              <Select
                value={contractData.fuel_type || 'none'}
                onValueChange={(value) =>
                  handleLeaseChange('fuel_type', value === 'none' ? null : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {FUEL_TYPES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Row 2: Monthly + Annual Commitment */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Monthly Commitment (gal)</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g., 500"
                  value={contractData.fuel_commitment_gallons_monthly ?? ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'fuel_commitment_gallons_monthly',
                      e.target.value ? parseInt(e.target.value, 10) : null
                    )
                  }
                  disabled={!contractData.fuel_type}
                />
              </div>
              <div className="space-y-2">
                <Label>Annual Commitment (gal)</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="e.g., 6000"
                  value={contractData.fuel_commitment_gallons_annual ?? ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'fuel_commitment_gallons_annual',
                      e.target.value ? parseInt(e.target.value, 10) : null
                    )
                  }
                  disabled={!contractData.fuel_type}
                />
              </div>
            </div>
            {/* Row 3: Price Type + Price/Gallon + Differential */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Price Type</Label>
                <Select
                  value={contractData.fuel_price_type || 'none'}
                  onValueChange={(value) =>
                    handleLeaseChange('fuel_price_type', value === 'none' ? null : value)
                  }
                  disabled={!contractData.fuel_type}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {FUEL_PRICE_TYPES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price/Gallon ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g., 5.50"
                  value={contractData.fuel_price_per_gallon ?? ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'fuel_price_per_gallon',
                      e.target.value ? parseFloat(e.target.value) : null
                    )
                  }
                  disabled={!contractData.fuel_type}
                />
              </div>
              <div className="space-y-2">
                <Label>Differential ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 0.25"
                  value={contractData.fuel_price_differential ?? ''}
                  onChange={(e) =>
                    handleLeaseChange(
                      'fuel_price_differential',
                      e.target.value ? parseFloat(e.target.value) : null
                    )
                  }
                  disabled={!contractData.fuel_type}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Note */}
      <div className="text-xs text-muted-foreground text-center">
        {isTransient
          ? 'Transient stays are billed based on nightly rates. You can extend or shorten the stay later.'
          : 'Lease contracts include monthly billing. Rates will be determined based on unit selection.'}
      </div>
    </div>
  )
}

export default ContractStep
